import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, Edit, Trash2, ArrowLeft, Eye, EyeOff, Upload, X, Image, ChevronUp, ChevronDown, GripVertical, Save } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve image URLs - handles both relative and absolute paths
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

const AdminNews = () => {
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [reorderedArticles, setReorderedArticles] = useState([]);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: 'Community Events',
    image: '',
    author: 'Admin'
  });

  const categories = ['Community Events', 'Programs', 'Success Stories', 'Announcements'];

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchArticles();
  }, [navigate]);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/news?published_only=false`);
      setArticles(response.data);
    } catch (error) {
      toast.error("Failed to fetch news articles");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    setIsUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/uploads/image`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const imageUrl = `${BACKEND_URL}${response.data.url}`;
        setFormData({ ...formData, image: imageUrl });
        toast.success('Image uploaded successfully!');
      }
    } catch (error) {
      toast.error('Failed to upload image. Please try again.');
      setImagePreview(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData({ ...formData, image: '' });
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      if (editingArticle) {
        await axios.put(
          `${BACKEND_URL}/api/news/${editingArticle.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Article updated successfully");
      } else {
        await axios.post(
          `${BACKEND_URL}/api/news`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Article created successfully");
      }
      setShowForm(false);
      setEditingArticle(null);
      resetForm();
      fetchArticles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save article");
    }
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      excerpt: article.excerpt,
      content: article.content,
      category: article.category,
      image: article.image || '',
      author: article.author
    });
    setImagePreview(article.image || null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(
        `${BACKEND_URL}/api/news/${articleId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Article deleted successfully");
      fetchArticles();
    } catch (error) {
      toast.error("Failed to delete article");
    }
  };

  const togglePublish = async (article) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(
        `${BACKEND_URL}/api/news/${article.id}`,
        { is_published: !article.is_published },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Article ${article.is_published ? 'unpublished' : 'published'} successfully`);
      fetchArticles();
    } catch (error) {
      toast.error("Failed to update article");
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      category: 'Community Events',
      image: '',
      author: 'Admin'
    });
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCancelEdit = () => {
    setShowForm(false);
    setEditingArticle(null);
    resetForm();
  };

  // Reorder functions
  const enterReorderMode = () => {
    setReorderedArticles([...articles]);
    setIsReorderMode(true);
  };

  const exitReorderMode = () => {
    setIsReorderMode(false);
    setReorderedArticles([]);
  };

  const moveArticle = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= reorderedArticles.length) return;
    
    const newArticles = [...reorderedArticles];
    [newArticles[index], newArticles[newIndex]] = [newArticles[newIndex], newArticles[index]];
    setReorderedArticles(newArticles);
  };

  const saveOrder = async () => {
    const token = localStorage.getItem('admin_token');
    setIsSavingOrder(true);
    
    try {
      const orders = reorderedArticles.map((article, index) => ({
        article_id: article.id,
        display_order: index + 1
      }));
      
      await axios.post(
        `${BACKEND_URL}/api/news/reorder`,
        { orders },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Article order saved successfully!');
      setIsReorderMode(false);
      fetchArticles(); // Refresh to get the updated order
    } catch (error) {
      toast.error('Failed to save article order');
    } finally {
      setIsSavingOrder(false);
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            {showForm ? (
              <Button
                onClick={handleCancelEdit}
                variant="outline"
                className="border-[#007A7A] text-[#007A7A]"
                data-testid="back-to-news-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to News List
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/admin/dashboard')}
                variant="outline"
                className="border-[#007A7A] text-[#007A7A]"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            )}
            <h1 className="text-3xl font-bold text-gray-900">
              {showForm 
                ? (editingArticle ? `Edit: ${editingArticle.title}` : 'Add New Article') 
                : (isReorderMode ? 'Reorder Articles' : 'Manage News & Blog')}
            </h1>
          </div>
          {!showForm && !isReorderMode && (
            <div className="flex space-x-3">
              <Button
                onClick={enterReorderMode}
                variant="outline"
                className="border-[#007A7A] text-[#007A7A]"
                data-testid="reorder-articles-btn"
                disabled={articles.length < 2}
              >
                <GripVertical className="w-4 h-4 mr-2" />
                Reorder Articles
              </Button>
              <Button
                onClick={() => {
                  setShowForm(true);
                  setEditingArticle(null);
                  resetForm();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="bg-[#007A7A] hover:bg-[#007A7A]/90"
                data-testid="add-article-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Article
              </Button>
            </div>
          )}
          {isReorderMode && (
            <div className="flex space-x-3">
              <Button
                onClick={exitReorderMode}
                variant="outline"
                className="border-gray-400 text-gray-600"
                data-testid="cancel-reorder-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={saveOrder}
                disabled={isSavingOrder}
                className="bg-green-600 hover:bg-green-700"
                data-testid="save-order-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {isSavingOrder ? 'Saving...' : 'Save Order'}
              </Button>
            </div>
          )}
        </div>

        {showForm && (
          <Card className="mb-8" data-testid="article-form">
            <CardHeader>
              <CardTitle>{editingArticle ? 'Edit Article' : 'Add New Article'}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                      data-testid="article-title-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                      data-testid="article-category-select"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Author</label>
                  <input
                    type="text"
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                    data-testid="article-author-input"
                  />
                </div>

                {/* Image Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Article Image</label>
                  
                  {/* Current Image Preview */}
                  {(imagePreview || formData.image) && (
                    <div className="mb-4 relative inline-block">
                      <img
                        src={imagePreview || resolveImageUrl(formData.image)}
                        alt="Article preview"
                        className="w-64 h-40 object-cover rounded-lg border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        data-testid="remove-image-btn"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Upload Button */}
                  {!imagePreview && !formData.image && (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-[#007A7A] hover:bg-[#007A7A]/5 transition-all duration-200"
                    >
                      <Image className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 mb-2">Click to upload an image</p>
                      <p className="text-xs text-gray-500">JPG, PNG, GIF, or WebP (max 5MB)</p>
                    </div>
                  )}

                  {/* Hidden File Input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    className="hidden"
                    data-testid="article-image-upload"
                  />

                  {/* Upload/Change Button */}
                  {(imagePreview || formData.image) && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="mt-2"
                      data-testid="change-image-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {isUploading ? 'Uploading...' : 'Change Image'}
                    </Button>
                  )}

                  {isUploading && (
                    <div className="mt-2 flex items-center text-sm text-gray-600">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#007A7A] mr-2"></div>
                      Uploading image...
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Excerpt *</label>
                  <textarea
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    required
                    rows="2"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                    placeholder="Brief summary of the article..."
                    data-testid="article-excerpt-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Content *</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    required
                    rows="8"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A]"
                    placeholder="Full article content..."
                    data-testid="article-content-input"
                  />
                </div>

                <div className="flex space-x-4">
                  <Button type="submit" className="bg-[#007A7A] hover:bg-[#007A7A]/90" data-testid="save-article-btn">
                    {editingArticle ? 'Update Article' : 'Create Article'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelEdit}
                    data-testid="cancel-article-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {!showForm && !isReorderMode && (
          <div className="space-y-4" data-testid="articles-list">
          {articles.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No articles yet. Click &quot;Add Article&quot; to create your first news article.
            </div>
          ) : (
            articles.map((article) => (
              <Card key={article.id} className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex gap-4 flex-1">
                      {/* Article Thumbnail */}
                      {article.image && (
                        <img
                          src={resolveImageUrl(article.image)}
                          alt={article.title}
                          className="w-24 h-16 object-cover rounded-lg flex-shrink-0"
                        />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-semibold text-gray-900">{article.title}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            article.is_published 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {article.is_published ? 'Published' : 'Draft'}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500 mb-2">
                          <span className="bg-gray-100 px-2 py-1 rounded">{article.category}</span>
                          <span>By {article.author}</span>
                          <span>{new Date(article.published_date).toLocaleDateString()}</span>
                        </div>
                        <p className="text-gray-600 line-clamp-2">{article.excerpt}</p>
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => togglePublish(article)}
                        className={article.is_published ? 'border-yellow-600 text-yellow-600' : 'border-green-600 text-green-600'}
                        data-testid={`toggle-publish-${article.id}`}
                      >
                        {article.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleEdit(article)}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid={`edit-article-${article.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(article.id)}
                        className="border-red-600 text-red-600 hover:bg-red-50"
                        data-testid={`delete-article-${article.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
        )}

        {/* Reorder Mode View */}
        {isReorderMode && (
          <div className="space-y-2" data-testid="reorder-list">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-blue-800 text-sm">
                <strong>Reorder Mode:</strong> Use the up/down arrows to change the order of articles. 
                Click "Save Order" when done. The order will be reflected on the News page immediately.
              </p>
            </div>
            {reorderedArticles.map((article, index) => (
              <Card key={article.id} className="border-2 border-dashed border-gray-300 hover:border-[#007A7A]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Position indicator */}
                    <div className="flex-shrink-0 w-8 h-8 bg-[#007A7A] text-white rounded-full flex items-center justify-center font-bold text-sm">
                      {index + 1}
                    </div>
                    
                    {/* Reorder buttons */}
                    <div className="flex flex-col gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => moveArticle(index, -1)}
                        disabled={index === 0}
                        className="h-7 w-7 p-0"
                        data-testid={`move-up-${article.id}`}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => moveArticle(index, 1)}
                        disabled={index === reorderedArticles.length - 1}
                        className="h-7 w-7 p-0"
                        data-testid={`move-down-${article.id}`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {/* Article info */}
                    <div className="flex gap-4 flex-1 items-center">
                      {article.image && (
                        <img
                          src={resolveImageUrl(article.image)}
                          alt={article.title}
                          className="w-16 h-12 object-cover rounded flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">{article.title}</h3>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          <span className="bg-gray-100 px-2 py-0.5 rounded text-xs">{article.category}</span>
                          <span>{new Date(article.published_date).toLocaleDateString()}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            article.is_published 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {article.is_published ? 'Published' : 'Draft'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Grip indicator */}
                    <GripVertical className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNews;
